import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpHelpService } from '../service/http-help.service';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { getDefaultReportLanguage } from 'src/app/shared/services/report-language-preference';
import { PrimeNGConfig } from 'primeng/api';

interface CustomerBalance {
  customer_id: number;
  customer_id_number: string;
  customer_name_ar: string;
  customer_name_en: string;
  customer_name_he: string;
  customer_code: string;
  vat_number: string;
  initial_balance: number;
  total_debit: number;
  total_credit: number;
  final_balance: number;
  period_transactions: number;
  balance: number; // Keep for backward compatibility
}

interface StatementInvoiceRow {
  is_account_entry?: boolean;
  entry_id?: number;
  invoice_id?: number;
  invoice_type?: number | string;
  invoice_type_name?: string;
  document_number?: string;
  invoice_date?: string;
  formatted_debit_amount?: string | number;
  formatted_credit_amount?: string | number;
  running_balance?: string | number;
  invoice_note?: string;
  invoice_link?: string | null;
  has_pdf_link?: boolean;
}

@Component({
  selector: 'app-customer-balances',
  templateUrl: './customer-balances.component.html',
  styleUrls: ['./customer-balances.component.scss']
})
export class CustomerBalancesComponent implements OnInit, OnDestroy {
  curentLang: string = '';
  customersData: CustomerBalance[] = [];
  filteredData: CustomerBalance[] = [];
  searchTerm: string = '';
  sortState: { key: string | null; direction: 'asc' | 'desc' | null } = {
    key: null,
    direction: null
  };

  // Date range properties
  fromDate: Date | null = null;
  toDate: Date | null = null;
  companyId: number | null = null;

  // Date validation
  dateRangeError: string = '';

  // Statistics
  statistics: any = null;

  // Customer details map for enriching data
  customersMap: Map<number, any> = new Map();

  // Account type selection
  accountType: boolean = false; // false = tax invoice, true = financial claim
  companyData: any[] = [];

  /** On-screen customer account statement (MD §6) */
  statementVisible = false;
  statementLoading = false;
  statementCustomer: CustomerBalance | null = null;
  statementRows: StatementInvoiceRow[] = [];
  statementInitialBalance: string | number = 0;
  statementError = '';

  private destroy$ = new Subject<void>();

  constructor(
    private spinner: NgxSpinnerService,
    public translate: TranslateService,
    private service: HttpHelpService,
    private router: Router,
    private primengConfig: PrimeNGConfig,
  ) {
    this.curentLang = localStorage.getItem('curentLang') || 'ar';
    this.initializeDateRange();
    this.loadCompanyId();
  }

  ngOnInit(): void {
    // Subscribe to language changes
    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.curentLang = event.lang;
        this.applyCalendarLocale(event.lang);
      });

    // Set initial language
    this.curentLang = this.translate.currentLang || localStorage.getItem('curentLang') || 'ar';
    this.applyCalendarLocale(this.curentLang);

    // Load company data first to determine default account type
    this.loadCompanyData();

    // Load all customers first to get VAT numbers, then load balances
    this.loadAllCustomers();
  }

  private applyCalendarLocale(lang: string): void {
    if (lang === 'ar') {
      this.primengConfig.setTranslation({
        dayNames: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'],
        dayNamesShort: ['أحد', 'إثن', 'ثلا', 'أرب', 'خمي', 'جمع', 'سبت'],
        dayNamesMin: ['ح', 'ن', 'ث', 'ر', 'خ', 'ج', 'س'],
        monthNames: [
          'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
          'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
        ],
        monthNamesShort: [
          'ينا', 'فبر', 'مار', 'أبر', 'ماي', 'يون',
          'يول', 'أغس', 'سبت', 'أكت', 'نوف', 'ديس',
        ],
        today: 'اليوم',
        clear: 'مسح',
        weekHeader: 'أسبوع',
        firstDayOfWeek: 6,
        dateFormat: 'dd/mm/yy',
      });
      return;
    }

    if (lang === 'he') {
      this.primengConfig.setTranslation({
        dayNames: ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'],
        dayNamesShort: ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'],
        dayNamesMin: ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'],
        monthNames: [
          'ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
          'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר',
        ],
        monthNamesShort: [
          'ינו', 'פבר', 'מרץ', 'אפר', 'מאי', 'יונ',
          'יול', 'אוג', 'ספט', 'אוק', 'נוב', 'דצמ',
        ],
        today: 'היום',
        clear: 'נקה',
        weekHeader: 'שבוע',
        firstDayOfWeek: 0,
        dateFormat: 'dd/mm/yy',
      });
      return;
    }

    this.primengConfig.setTranslation({
      dayNames: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      dayNamesShort: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      dayNamesMin: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
      monthNames: [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
      ],
      monthNamesShort: [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
      ],
      today: 'Today',
      clear: 'Clear',
      weekHeader: 'Wk',
      firstDayOfWeek: 0,
      dateFormat: 'dd/mm/yy',
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load all customers to create a map for enriching balance data
   */
  loadAllCustomers(): void {
    this.service.getAllCustomersForDropdown().subscribe({
      next: (res) => {
        // Service already normalises the response into a plain array.
        const customers: any[] = Array.isArray(res) ? res : [];

        // Create a map of customer_id -> customer details
        customers.forEach((customer: any) => {
          const customerId = customer.customer_id || customer.customer_customer_id;
          if (customerId) {
            this.customersMap.set(customerId, customer);
          }
        });

        // After customers are loaded, load balances if company and dates are ready
        if (this.companyId && this.fromDate && this.toDate) {
          this.loadCustomerBalances();
        }
      },
      error: (error) => {
        console.error('Error loading customers:', error);
        // Still try to load balances even if customers fail
        if (this.companyId && this.fromDate && this.toDate) {
          this.loadCustomerBalances();
        }
      }
    });
  }

  /**
   * Initialize date range to current year (Jan 1 to Dec 31)
   */
  initializeDateRange(): void {
    const currentYear = new Date().getFullYear();
    // First day of current year (January 1)
    this.fromDate = new Date(currentYear, 0, 1);
    // Last day of current year (December 31)
    this.toDate = new Date(currentYear, 11, 31);
  }

  /**
   * Get default from date (first day of current year)
   */
  getDefaultFromDate(): Date {
    const currentYear = new Date().getFullYear();
    return new Date(currentYear, 0, 1);
  }

  /**
   * Get default to date (last day of current year)
   */
  getDefaultToDate(): Date {
    const currentYear = new Date().getFullYear();
    return new Date(currentYear, 11, 31);
  }

  /**
   * Load company ID from localStorage
   */
  loadCompanyId(): void {
    const storedCompanyId = localStorage.getItem('companyDefault');
    if (storedCompanyId) {
      this.companyId = Number(storedCompanyId);
    } else {
      // Show error if no company is selected
      console.warn('No company selected. Please select a company first.');
    }
  }

  /**
   * Format date to YYYY-MM-DD
   */
  formatDate(date: Date | null): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Load customer balances from API
   */
  loadCustomerBalances(): void {
    if (!this.companyId) {
      if (this.curentLang === 'ar') {
        this.dateRangeError = 'يرجى اختيار المصلحة التجارية أولاً';
      } else if (this.curentLang === 'he') {
        this.dateRangeError = 'אנא בחר חברה תחילה';
      } else {
        this.dateRangeError = 'Please select a company first';
      }
      return;
    }

    if (!this.fromDate || !this.toDate) {
      if (this.curentLang === 'ar') {
        this.dateRangeError = 'يرجى اختيار نطاق التاريخ (من تاريخ وإلى تاريخ)';
      } else if (this.curentLang === 'he') {
        this.dateRangeError = 'אנא בחר טווח תאריכים (מתאריך ועד תאריך)';
      } else {
        this.dateRangeError = 'Please select a date range (from date and to date)';
      }
      return;
    }

    // Validate date range
    if (!this.validateDateRange()) {
      return;
    }

    this.spinner.show();
    const startDate = this.formatDate(this.fromDate);
    const endDate = this.formatDate(this.toDate);
    const lang = this.curentLang;

    this.service.getCompanyCustomersAccountSummary(this.companyId, startDate, endDate, lang, this.accountType).subscribe({
      next: (res) => {
        if (res && res.status === 200 && res.data) {
          // Store statistics
          this.statistics = res.data.statistics || null;

          // Extract customers_accounts array
          const customersAccounts = res.data.customers_accounts || [];

          if (customersAccounts.length > 0) {
            // Transform API response to CustomerBalance format
            // The API response has nested structure: customer_info and account_summary
            this.customersData = customersAccounts.map((account: any) => {
              const customerInfo = account.customer_info || {};
              const accountSummary = account.account_summary || {};
              const customerId = customerInfo.customer_id || 0;
              
              // Get customer details from map to enrich with VAT number
              const customerDetails = this.customersMap.get(customerId) || {};
              
              return {
                customer_id: customerId,
                customer_id_number: customerInfo.customer_id_number || 
                                    customerInfo.id_number || 
                                    customerDetails.customer_id_number || 
                                    customerDetails.id_number || 
                                    '',
                customer_name_ar: customerInfo.customer_name_ar || '',
                customer_name_en: customerInfo.customer_name_en || customerInfo.customer_name_ar || '',
                customer_name_he: customerInfo.customer_name_he || customerInfo.customer_name_ar || '',
                customer_code: customerInfo.customer_code || customerDetails.customer_code || customerId?.toString() || '',
                vat_number: customerInfo.vat_number || 
                           customerInfo.customer_vat_number || 
                           customerInfo.customer_vat_tax || 
                           customerInfo.id_number ||
                           customerDetails.customer_vat_number || 
                           customerDetails.customer_vat_tax || 
                           customerDetails.id_number || '',
                initial_balance: accountSummary.initial_balance !== undefined ? parseFloat(accountSummary.initial_balance) || 0 : 0,
                total_debit: accountSummary.total_debit !== undefined ? parseFloat(accountSummary.total_debit) || 0 : 0,
                total_credit: accountSummary.total_credit !== undefined ? parseFloat(accountSummary.total_credit) || 0 : 0,
                final_balance: accountSummary.final_balance !== undefined ? parseFloat(accountSummary.final_balance) || 0 : 0,
                period_transactions: accountSummary.period_transactions !== undefined ? parseInt(accountSummary.period_transactions) || 0 : 0,
                balance: accountSummary.final_balance !== undefined ? parseFloat(accountSummary.final_balance) || 0 : 0 // Keep for backward compatibility
              };
            });
            this.filteredData = [...this.customersData];
          } else {
            this.customersData = [];
            this.filteredData = [];
          }
        } else {
          this.customersData = [];
          this.filteredData = [];
        }
        this.spinner.hide();
      },
      error: (error) => {
        console.error('Error loading customer balances:', error);
        this.customersData = [];
        this.filteredData = [];
        this.spinner.hide();
      }
    });
  }

  /**
   * Validate date range
   */
  validateDateRange(): boolean {
    this.dateRangeError = '';
    
    if (!this.fromDate || !this.toDate) {
      return false;
    }

    // Reset time to compare dates only
    const from = new Date(this.fromDate);
    from.setHours(0, 0, 0, 0);
    const to = new Date(this.toDate);
    to.setHours(0, 0, 0, 0);

    if (from > to) {
      if (this.curentLang === 'ar') {
        this.dateRangeError = 'تاريخ البداية لا يمكن أن يكون بعد تاريخ النهاية';
      } else if (this.curentLang === 'he') {
        this.dateRangeError = 'תאריך התחלה לא יכול להיות אחרי תאריך סיום';
      } else {
        this.dateRangeError = 'Start date cannot be after end date';
      }
      return false;
    }

    // Clear error if validation passes
    this.dateRangeError = '';
    return true;
  }


  /**
   * Format input with automatic slashes (dd/mm/yyyy) - only when needed
   */
  formatDateInput(value: string, forceFormat: boolean = false): string {
    // If value already has slashes in correct positions, don't reformat while typing
    if (!forceFormat && value.includes('/')) {
      const parts = value.split('/');
      // If it already looks formatted (has slashes), return as-is
      if (parts.length <= 3 && parts.every(p => /^\d*$/.test(p))) {
        return value;
      }
    }
    
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Limit to 8 digits (ddmmyyyy)
    const limited = digits.substring(0, 8);
    
    // Add slashes automatically
    let formatted: string;
    if (limited.length <= 2) {
      formatted = limited;
    } else if (limited.length <= 4) {
      formatted = limited.substring(0, 2) + '/' + limited.substring(2);
    } else {
      formatted = limited.substring(0, 2) + '/' + limited.substring(2, 4) + '/' + limited.substring(4);
    }
    return formatted;
  }

  /**
   * Auto-correct invalid date values
   */
  correctDateValues(day: number, month: number, year: number): { day: number; month: number; year: number } {
    // Correct month (1-12)
    if (month < 1) month = 1;
    if (month > 12) month = 12;
    
    // Correct year - be smarter about it
    if (year < 100) {
      // 2-digit year: assume 20xx
      year += 2000;
    } else if (year < 1900) {
      // Very old year, use current year instead
      year = new Date().getFullYear();
    } else if (year > 2100) {
      // Future year beyond 2100, use current year instead of clamping to 1900
      year = new Date().getFullYear();
    }
    
    // Get days in month
    const daysInMonth = new Date(year, month, 0).getDate();
    
    // Correct day
    if (day < 1) day = 1;
    if (day > daysInMonth) day = daysInMonth;
    
    return { day, month, year };
  }

  /**
   * Parse and auto-correct date string in dd/mm/yy or dd/mm/yyyy format
   */
  parseDateString(dateString: string): Date | null {
    if (!dateString || dateString.trim() === '') {
      return null;
    }

    // Remove any whitespace and clean the string
    const cleaned = dateString.trim().replace(/\s+/g, '');
    // Try to match date pattern (flexible - allows partial dates)
    const datePattern = /^(\d{1,2})\/?(\d{0,2})\/?(\d{0,4})$/;
    const match = cleaned.match(datePattern);
    if (!match) {
      return null;
    }

    let day = parseInt(match[1] || '1', 10);
    let month = parseInt(match[2] || '1', 10);
    let year = parseInt(match[3] || new Date().getFullYear().toString(), 10);
    // If no year provided, use current year
    if (!match[3] || match[3] === '') {
      year = new Date().getFullYear();
      }
    
    // Auto-correct invalid values
    const corrected = this.correctDateValues(day, month, year);
    day = corrected.day;
    month = corrected.month;
    year = corrected.year;
    // Create date (month is 0-indexed in Date constructor)
    const date = new Date(year, month - 1, day);
    // Validate the date is correct
    if (date.getDate() === day && 
        date.getMonth() === month - 1 && 
        date.getFullYear() === year) {
      return date;
    }
    
    return null;
  }

  /**
   * Handle manual input for from date - minimal interference
   */
  onFromDateInput(event: any): void {
    const input = event?.target || event;
    if (!input) {
      return;
    }
    
    let inputValue = input.value || '';
    // Only format if user typed digits without slashes (e.g., typing "101" should become "10/1")
    // But don't reformat if they already have slashes in correct positions
    if (!inputValue.includes('/') && /^\d+$/.test(inputValue) && inputValue.length > 2) {
      const formatted = this.formatDateInput(inputValue, true);
      if (formatted !== inputValue) {
        const cursorPos = input.selectionStart || 0;
        input.value = formatted;
        // Maintain cursor position
        const newPos = Math.min(cursorPos + (formatted.length - inputValue.length), formatted.length);
        setTimeout(() => {
          input.setSelectionRange(newPos, newPos);
        }, 0);
      }
    }
    
    // Don't parse while typing - let user finish typing first
    // Only validate on blur
    }

  /**
   * Handle blur event for from date - final validation and correction
   */
  onFromDateBlur(event: any): void {
    const input = event?.target || event;
    if (!input) {
      return;
    }
    
    let inputValue = (input.value || '').trim();
    if (!inputValue) {
      this.fromDate = this.getDefaultFromDate();
      const day = String(this.fromDate.getDate()).padStart(2, '0');
      const month = String(this.fromDate.getMonth() + 1).padStart(2, '0');
      const year = this.fromDate.getFullYear();
      input.value = `${day}/${month}/${year}`;
      return;
    }

    // Force format on blur to ensure proper format
    const formatted = this.formatDateInput(inputValue, true);
    // Parse and auto-correct
    let parsedDate = this.parseDateString(formatted);
    // If parsing failed, try to fix common issues
    if (!parsedDate && formatted.length > 0) {
      // Try with current year if year is missing
      const parts = formatted.split('/');
      if (parts.length === 2) {
        const withYear = parts[0] + '/' + parts[1] + '/' + new Date().getFullYear();
        parsedDate = this.parseDateString(withYear);
        }
    }
    
    if (parsedDate) {
      this.fromDate = parsedDate;
      // Update input with correctly formatted date
      const day = String(parsedDate.getDate()).padStart(2, '0');
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const year = parsedDate.getFullYear();
      const finalValue = `${day}/${month}/${year}`;
      input.value = finalValue;
      
      if (this.fromDate && this.toDate) {
        this.validateDateRange();
      }
    } else {
      // Invalid date - reset to default
      this.fromDate = this.getDefaultFromDate();
      const day = String(this.fromDate.getDate()).padStart(2, '0');
      const month = String(this.fromDate.getMonth() + 1).padStart(2, '0');
      const year = this.fromDate.getFullYear();
      input.value = `${day}/${month}/${year}`;
    }
  }

  /**
   * Handle manual input for to date - minimal interference
   */
  onToDateInput(event: any): void {
    const input = event?.target || event;
    if (!input) {
      return;
    }
    
    let inputValue = input.value || '';
    // Only format if user typed digits without slashes (e.g., typing "101" should become "10/1")
    // But don't reformat if they already have slashes in correct positions
    if (!inputValue.includes('/') && /^\d+$/.test(inputValue) && inputValue.length > 2) {
      const formatted = this.formatDateInput(inputValue, true);
      if (formatted !== inputValue) {
        const cursorPos = input.selectionStart || 0;
        input.value = formatted;
        // Maintain cursor position
        const newPos = Math.min(cursorPos + (formatted.length - inputValue.length), formatted.length);
        setTimeout(() => {
          input.setSelectionRange(newPos, newPos);
        }, 0);
      }
    }
    
    // Don't parse while typing - let user finish typing first
    // Only validate on blur
    }

  /**
   * Handle blur event for to date - final validation and correction
   */
  onToDateBlur(event: any): void {
    const input = event?.target || event;
    if (!input) {
      return;
    }
    
    let inputValue = (input.value || '').trim();
    if (!inputValue) {
      this.toDate = this.getDefaultToDate();
      const day = String(this.toDate.getDate()).padStart(2, '0');
      const month = String(this.toDate.getMonth() + 1).padStart(2, '0');
      const year = this.toDate.getFullYear();
      input.value = `${day}/${month}/${year}`;
      return;
    }

    // Force format on blur to ensure proper format
    const formatted = this.formatDateInput(inputValue, true);
    // Parse and auto-correct
    let parsedDate = this.parseDateString(formatted);
    // If parsing failed, try to fix common issues
    if (!parsedDate && formatted.length > 0) {
      // Try with current year if year is missing
      const parts = formatted.split('/');
      if (parts.length === 2) {
        const withYear = parts[0] + '/' + parts[1] + '/' + new Date().getFullYear();
        parsedDate = this.parseDateString(withYear);
        }
    }
    
    if (parsedDate) {
      this.toDate = parsedDate;
      // Update input with correctly formatted date
      const day = String(parsedDate.getDate()).padStart(2, '0');
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
      const year = parsedDate.getFullYear();
      const finalValue = `${day}/${month}/${year}`;
      input.value = finalValue;
      
      if (this.fromDate && this.toDate) {
        this.validateDateRange();
      }
    } else {
      // Invalid date - reset to default
      this.toDate = this.getDefaultToDate();
      const day = String(this.toDate.getDate()).padStart(2, '0');
      const month = String(this.toDate.getMonth() + 1).padStart(2, '0');
      const year = this.toDate.getFullYear();
      input.value = `${day}/${month}/${year}`;
    }
  }

  /**
   * Handle from date change
   */
  onFromDateChange(date: Date | null): void {
    // If cleared (null), reset to default value
    if (date === null) {
      this.fromDate = this.getDefaultFromDate();
    } else {
    this.fromDate = date;
    }
    
    if (this.fromDate && this.toDate) {
      this.validateDateRange();
    } else {
      this.dateRangeError = '';
    }
  }

  /**
   * Handle to date change
   */
  onToDateChange(date: Date | null): void {
    // If cleared (null), reset to default value
    if (date === null) {
      this.toDate = this.getDefaultToDate();
    } else {
    this.toDate = date;
    }
    
    if (this.fromDate && this.toDate) {
      this.validateDateRange();
    } else {
      this.dateRangeError = '';
    }
  }

  /**
   * Get max date for from date (should not exceed toDate)
   */
  getMaxFromDate(): Date | null {
    return this.toDate || null;
    }

  /**
   * Get min date for to date (should not be before fromDate)
   */
  getMinToDate(): Date | null {
    return this.fromDate || null;
  }

  /**
   * Get customer name based on current language
   */
  getCustomerName(customer: CustomerBalance): string {
    if (this.curentLang === 'en') return customer.customer_name_en || customer.customer_name_ar || customer.customer_name_he;
    if (this.curentLang === 'ar') return customer.customer_name_ar || customer.customer_name_en || customer.customer_name_he;
    return customer.customer_name_he || customer.customer_name_ar || customer.customer_name_en;
  }

  /**
   * Format balance with English number format
   */
  formatBalance(value: number): string {
    const formatter = new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const formatted = formatter.format(Math.abs(value));
    return `${value < 0 ? '-' : ''}₪ ${formatted}`;
  }

  /**
   * Search functionality
   */
  onSearch(): void {
    const term = this.searchTerm.toLowerCase().trim();
    if (!term) {
      this.filteredData = [...this.customersData];
      return;
    }

    this.filteredData = this.customersData.filter(customer => {
      const name = this.getCustomerName(customer).toLowerCase();
      const code = customer.customer_code.toLowerCase();
      const vat = customer.vat_number.toLowerCase();
      const customerId = customer.customer_id.toString();
      const customerIdNumber = (customer.customer_id_number || '').toLowerCase();
      return name.includes(term) || code.includes(term) || vat.includes(term) || customerId.includes(term) || customerIdNumber.includes(term);
    });

    // Apply current sort if any
    if (this.sortState.key) {
      this.applySort(this.sortState.key, this.sortState.direction || 'asc');
    }
  }

  /**
   * Sort functionality
   */
  onSort(key: string): void {
    if (this.sortState.key === key) {
      // Toggle direction
      this.sortState.direction = this.sortState.direction === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortState.key = key;
      this.sortState.direction = 'asc';
    }
    this.applySort(key, this.sortState.direction);
  }

  /**
   * Apply sort to filtered data
   */
  applySort(key: string, direction: 'asc' | 'desc'): void {
    this.filteredData = [...this.filteredData].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (key) {
        case 'name':
          aVal = this.getCustomerName(a);
          bVal = this.getCustomerName(b);
          break;
        case 'id':
          aVal = a.customer_id;
          bVal = b.customer_id;
          break;
        case 'initial_balance':
          aVal = a.initial_balance;
          bVal = b.initial_balance;
          break;
        case 'debit':
          aVal = a.total_debit;
          bVal = b.total_debit;
          break;
        case 'credit':
          aVal = a.total_credit;
          bVal = b.total_credit;
          break;
        case 'balance':
          aVal = a.final_balance;
          bVal = b.final_balance;
          break;
        case 'transactions':
          aVal = a.period_transactions;
          bVal = b.period_transactions;
          break;
        default:
          return 0;
      }

      // Numeric comparison for numeric fields
      if (['balance', 'initial_balance', 'debit', 'credit', 'transactions', 'id'].includes(key)) {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // String comparison for others
      const aStr = String(aVal || '').toLowerCase();
      const bStr = String(bVal || '').toLowerCase();
      const comparison = aStr.localeCompare(bStr, this.curentLang === 'ar' ? 'ar' : 'en', {
        numeric: true,
        sensitivity: 'base'
      });

      return direction === 'asc' ? comparison : -comparison;
    });
  }

  /**
   * Get sort class for styling
   */
  getSortClass(key: string): string {
    if (this.sortState.key !== key) return '';
    return this.sortState.direction === 'asc' ? 'asc' : 'desc';
  }

  /**
   * Calculate total balance
   */
  getTotalBalance(): number {
    return this.filteredData.reduce((sum, customer) => sum + customer.final_balance, 0);
  }

  /**
   * Calculate total debit
   */
  getTotalDebit(): number {
    return this.filteredData.reduce((sum, customer) => sum + customer.total_debit, 0);
  }

  /**
   * Calculate total credit
   */
  getTotalCredit(): number {
    return this.filteredData.reduce((sum, customer) => sum + customer.total_credit, 0);
  }

  /**
   * Open on-screen customer statement (includes account entries from backend).
   */
  onBalanceClick(customer: CustomerBalance): void {
    if (!this.companyId || !this.fromDate || !this.toDate) {
      console.error('Company ID and date range are required');
      return;
    }

    this.statementCustomer = customer;
    this.statementVisible = true;
    this.statementLoading = true;
    this.statementRows = [];
    this.statementError = '';
    this.statementInitialBalance = 0;
    this.spinner.show();

    const startDate = this.formatDate(this.fromDate);
    const endDate = this.formatDate(this.toDate);
    const lang = this.curentLang || getDefaultReportLanguage();

    this.service
      .getCustomerAccountSummary(
        customer.customer_id,
        this.companyId,
        startDate,
        endDate,
        lang,
        this.accountType,
      )
      .subscribe({
        next: (res) => {
          this.spinner.hide();
          this.statementLoading = false;
          if (res?.status === 200 && res?.data) {
            const data = res.data;
            this.statementInitialBalance =
              data.initial_balance ??
              data.opening_balance ??
              data.account_summary?.initial_balance ??
              0;
            const invoices =
              data.invoices ||
              data.account_summary?.invoices ||
              data.formattedInvoices ||
              [];
            this.statementRows = Array.isArray(invoices) ? invoices : [];
          } else {
            this.statementError =
              res?.messageAr ||
              res?.messageEn ||
              res?.messageHe ||
              this.translate.instant('customerBalances.statement.loadError');
          }
        },
        error: (error) => {
          console.error('Error loading customer account summary:', error);
          this.spinner.hide();
          this.statementLoading = false;
          this.statementError =
            this.curentLang === 'ar'
              ? error?.error?.messageAr
              : this.curentLang === 'he'
                ? error?.error?.messageHe
                : error?.error?.messageEn
                  || this.translate.instant('customerBalances.statement.loadError');
        },
      });
  }

  closeStatement(): void {
    this.statementVisible = false;
    this.statementCustomer = null;
    this.statementRows = [];
    this.statementError = '';
  }

  isAccountEntryRow(row: StatementInvoiceRow): boolean {
    return row?.is_account_entry === true;
  }

  isDebitAccountEntry(row: StatementInvoiceRow): boolean {
    if (!this.isAccountEntryRow(row)) {
      return false;
    }
    if (Number(row.invoice_type) === 901) {
      return true;
    }
    const debit = Number(String(row.formatted_debit_amount ?? '').replace(/,/g, ''));
    return Number.isFinite(debit) && debit > 0;
  }

  isCreditAccountEntry(row: StatementInvoiceRow): boolean {
    if (!this.isAccountEntryRow(row)) {
      return false;
    }
    if (Number(row.invoice_type) === 902) {
      return true;
    }
    const credit = Number(String(row.formatted_credit_amount ?? '').replace(/,/g, ''));
    return Number.isFinite(credit) && credit > 0;
  }

  canOpenRowPdf(row: StatementInvoiceRow): boolean {
    if (this.isAccountEntryRow(row)) {
      return false;
    }
    if (row.has_pdf_link === false) {
      return false;
    }
    return !!(row.invoice_link && String(row.invoice_link).trim());
  }

  openRowPdf(row: StatementInvoiceRow, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    if (!this.canOpenRowPdf(row) || !row.invoice_link) {
      return;
    }
    window.open(row.invoice_link, '_blank');
  }

  getStatementRowTypeLabel(row: StatementInvoiceRow): string {
    if (row.invoice_type_name) {
      return String(row.invoice_type_name);
    }
    if (this.isAccountEntryRow(row)) {
      const type = Number(row.invoice_type);
      if (type === 901) {
        return this.translate.instant('customerBalances.statement.debitEntry');
      }
      if (type === 902) {
        return this.translate.instant('customerBalances.statement.creditEntry');
      }
      return this.translate.instant('customerBalances.statement.accountEntry');
    }
    return row.document_number || '—';
  }

  formatStatementAmount(value: string | number | null | undefined, treatZeroAsDash = true): string {
    if (value == null || value === '') {
      return '—';
    }
    const num = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
    if (!Number.isFinite(num)) {
      return String(value);
    }
    if (treatZeroAsDash && num === 0) {
      return '—';
    }
    return this.formatBalance(num);
  }

  /** Download full statement PDF (backend merges account entries). */
  downloadStatementPdf(): void {
    if (!this.statementCustomer || !this.companyId || !this.fromDate || !this.toDate) {
      return;
    }

    this.spinner.show();
    const startDate = this.formatDate(this.fromDate);
    const endDate = this.formatDate(this.toDate);
    const lang = getDefaultReportLanguage();
    const customer = this.statementCustomer;

    this.service
      .getCustomerAccountStatementPdf(
        customer.customer_id,
        this.companyId,
        startDate,
        endDate,
        lang,
        this.accountType,
      )
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          const newWindow = window.open(url, '_blank');
          setTimeout(() => {
            if (newWindow) {
              try {
                if (!newWindow.closed) {
                  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                } else {
                  window.URL.revokeObjectURL(url);
                }
              } catch {
                window.URL.revokeObjectURL(url);
              }
            } else {
              const link = document.createElement('a');
              link.href = url;
              link.download = `كشف_حساب_${customer.customer_code}_${startDate}_${endDate}.pdf`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              setTimeout(() => window.URL.revokeObjectURL(url), 100);
            }
          }, 100);
          this.spinner.hide();
        },
        error: (error) => {
          console.error('Error opening customer statement PDF:', error);
          this.spinner.hide();
        },
      });
  }

  /**
   * Export to Excel
   */
  exportToExcel(): void {
    const headers = [
      this.translate.instant('customerBalances.table.columns.customer_name'),
      this.translate.instant('customerBalances.table.columns.customer_id'),
      this.translate.instant('customerBalances.table.columns.opening_balance'),
      this.translate.instant('customerBalances.table.columns.debit'),
      this.translate.instant('customerBalances.table.columns.credit'),
      this.translate.instant('customerBalances.table.columns.current_balance'),
      this.translate.instant('customerBalances.table.columns.movements')
    ];

    const data = this.filteredData.map(customer => [
      this.getCustomerName(customer),
      customer.customer_id_number || customer.customer_id.toString(),
      this.formatBalance(customer.initial_balance),
      this.formatBalance(customer.total_debit),
      this.formatBalance(customer.total_credit),
      this.formatBalance(customer.final_balance),
      customer.period_transactions.toString()
    ]);

    // Add total row
    const totalDebit = this.getTotalDebit();
    const totalCredit = this.getTotalCredit();
    const totalBalance = this.getTotalBalance();
    data.push([
      this.translate.instant('customerBalances.table.total.label'),
      '',
      '',
      this.formatBalance(totalDebit),
      this.formatBalance(totalCredit),
      this.formatBalance(totalBalance),
      ''
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const workbook = XLSX.utils.book_new();
    const reportTitle = this.translate.instant('customerBalances.title');
    XLSX.utils.book_append_sheet(workbook, worksheet, reportTitle.substring(0, 31));

    const fileBase = reportTitle.replace(/\s+/g, '_');
    const fileName = `${fileBase}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }

  /**
   * Load company data to determine default account type
   */
  loadCompanyData(): void {
    this.service.getAllActiveCompanyByMerchant().subscribe({
      next: (res) => {
        if (Array.isArray(res)) {
          this.companyData = res;
        } else if (res && res.data && Array.isArray(res.data)) {
          this.companyData = res.data;
        }
        // Set default account type after loading company data
        this.accountType = this.getDefaultAccountType();
      },
      error: (error) => {
        console.error('Error loading company data:', error);
        // Set default to tax invoice if error occurs
        this.accountType = false;
      }
    });
  }

  /**
   * Gets the default account type based on the selected company's type.
   * Returns false for tax invoice (مصلحة تجارية or مشتغل), true for financial claim (جمعية or معفي).
   * @returns {boolean} - false for tax invoice, true for financial claim
   */
  getDefaultAccountType(): boolean {
    if (!this.companyId || !this.companyData || this.companyData.length === 0) {
      // Default to tax invoice if no company data
      return false;
    }

    const selectedCompany = this.companyData.find((company: any) => company.company_id === this.companyId);
    if (!selectedCompany || !selectedCompany.companyType) {
      // Default to tax invoice if no company type data
      return false;
    }

    const typeNameAr = selectedCompany.companyType.type_name_ar || '';
    
    // Check if company type is "جمعية" (association) or "معفي" (exempt) -> financial claim (true)
    // Otherwise (مصلحة تجارية, مشتغل, etc.) -> tax invoice (false)
    if (typeNameAr.includes('جمعية') || typeNameAr.includes('معفي')) {
      return true; // Financial claim
    }
    
    return false; // Tax invoice (default for مصلحة تجارية, مشتغل, etc.)
  }


  /**
   * Handle account type change - reload data with new account type
   */
  onAccountTypeChange(): void {
    // Reload customer balances with the new account type
    if (this.companyId && this.fromDate && this.toDate) {
      this.loadCustomerBalances();
    }
  }

  /**
   * Export full report as PDF (uses current account type selection)
   */
  exportToPdf(): void {
    if (!this.companyId || !this.fromDate || !this.toDate) {
      console.error('Company ID and date range are required');
      return;
    }

    // Use current account type selection and download PDF directly
    this.downloadPdf();
  }

  /**
   * Open PDF with selected account type in new tab
   */
  downloadPdf(): void {
    if (!this.companyId || !this.fromDate || !this.toDate) {
      console.error('Company ID and date range are required');
      return;
    }

    this.spinner.show();
    const startDate = this.formatDate(this.fromDate);
    const endDate = this.formatDate(this.toDate);
    const lang = getDefaultReportLanguage();

    this.service.getCompanyCustomersAccountStatementPdf(this.companyId, startDate, endDate, lang, this.accountType).subscribe({
      next: (blob: Blob) => {
        // Open PDF in new tab
        const url = window.URL.createObjectURL(blob);
        const newWindow = window.open(url, '_blank');
        
        // Clean up the object URL after a delay to allow the browser to load it
        // Note: We delay cleanup to ensure the PDF loads in the new tab
        setTimeout(() => {
          if (newWindow) {
            // Check if window is still open before revoking
            try {
              if (!newWindow.closed) {
                // Delay cleanup further to ensure PDF is fully loaded
                setTimeout(() => {
                  window.URL.revokeObjectURL(url);
                }, 1000);
              } else {
                window.URL.revokeObjectURL(url);
              }
            } catch (e) {
              // Cross-origin or closed window, cleanup immediately
              window.URL.revokeObjectURL(url);
            }
          } else {
            // If popup was blocked, fallback to download
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.translate.instant('customerBalances.title').replace(/\s+/g, '_')}_${startDate}_${endDate}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
            setTimeout(() => {
        window.URL.revokeObjectURL(url);
            }, 100);
          }
        }, 100);
        this.spinner.hide();
      },
      error: (error) => {
        console.error('Error opening PDF:', error);
        this.spinner.hide();
      }
    });
  }

  /**
   * Print report
   */
  printReport(): void {
    window.print();
  }
}
